/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';

import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Avatar,
  Fade,
  CardHeader,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  getDoctors,
  getDoctorsAvailability,
  addDoctorAvailability,
  updateDoctorAvailabilityById,
  deleteDoctorAvailabilityById,
  getDoctorExceptions,
  addDoctorException,
  updateDoctorExceptionById,
  deleteDoctorExceptionById,
} from '../services/api_service';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useAlert } from '../AlertContext';

const DAYS_OF_WEEK = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miércoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sábado' },
];

const TIME_SLOTS = Array.from({ length: 13 }, (_, i) => {
  const hour = (i + 6).toString().padStart(2, '0'); // Start from 6 and go up to 18
  return `${hour}:00`;
});

export default function DoctorAvailability() {
  const [doctors, setDoctors] = useState([]);
  const [modalViewOpen, setModalViewOpen] = useState(false);
  const [viewDoctor, setViewDoctor] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [exceptions, setExceptions] = useState([]); // New state for exceptions
  // eslint-disable-next-line no-unused-vars
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // Renamed from editingAvailability
  const [loading, setLoading] = useState(true);
  const [formType, setFormType] = useState('availability'); // 'availability' or 'exception'
  const [formData, setFormData] = useState({
    // Common fields for both availability and exceptions
    doctor_id: '',
    // For recurring availability
    day_of_week: '', // Renamed from 'day'
    hstart: '',
    hend: '',
    unit: '',
    start_date: null,
    end_date: null,
    // For exceptions
    exception_date: null,
    exception_type: '', // 'available_override', 'unavailable_full_day', 'unavailable_partial_day'
    time_slots: [], // Array of {hstart, hend, unit}
    reason: '',
  });
  const { showAlert } = useAlert();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [doctorsResponse, availabilityResponse, exceptionsResponse] =
        await Promise.all([
          getDoctors(),
          getDoctorsAvailability(),
          getDoctorExceptions(), // Fetch exceptions
        ]);

      if (doctorsResponse?.result) {
        setDoctors(doctorsResponse.result);
      }
      if (availabilityResponse?.result) {
        setAvailability(availabilityResponse.result);
      }
      if (exceptionsResponse?.result) {
        setExceptions(exceptionsResponse.result); // Set exceptions state
      }
    } catch (error) {
      showAlert('Error al cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (
    doctor = null,
    item = null,
    type = 'availability'
  ) => {
    setFormType(type);
    if (item) {
      // Editing existing item (availability or exception)
      setEditingItem(item);
      if (type === 'availability') {
        setFormData({
          doctor_id: item.doctor_id,
          day_of_week: item.day_of_week,
          hstart: item.hstart,
          hend: item.hend,
          unit: item.unit,
          start_date: item.start_date ? item.start_date.substring(0, 10) : null,
          end_date: item.end_date ? item.end_date.substring(0, 10) : null,
          exception_date: null,
          exception_type: '',
          time_slots: [],
          reason: '',
        });
      } else if (type === 'exception') {
        setFormData({
          doctor_id: item.doctor_id,
          day_of_week: '',
          hstart: '',
          hend: '',
          unit: '',
          start_date: null,
          end_date: null,
          exception_date: item.exception_date
            ? item.exception_date.substring(0, 10)
            : null,
          exception_type: item.exception_type,
          time_slots: item.time_slots || [],
          reason: item.reason || '',
        });
      }
    } else {
      // Adding new item
      setEditingItem(null);
      setFormData({
        doctor_id: doctor?.id || '',
        day_of_week: '',
        hstart: '',
        hend: '',
        unit: '',
        start_date: null,
        end_date: null,
        exception_date: null,
        exception_type: '',
        time_slots: [],
        reason: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({
      doctor_id: '',
      day_of_week: '',
      hstart: '',
      hend: '',
      unit: '',
      start_date: null,
      end_date: null,
      exception_date: null,
      exception_type: '',
      time_slots: [],
      reason: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (formType === 'availability') {
        const payload = {
          doctor_id: formData.doctor_id,
          day_of_week: formData.day_of_week,
          hstart: formData.hstart,
          hend: formData.hend,
          unit: formData.unit,
          start_date: formData.start_date,
          end_date: formData.end_date,
        };
        if (editingItem) {
          await updateDoctorAvailabilityById(editingItem.id, payload);
          showAlert('Disponibilidad actualizada exitosamente', 'success');
        } else {
          await addDoctorAvailability(payload);
          showAlert('Disponibilidad añadida exitosamente', 'success');
        }
      } else if (formType === 'exception') {
        const payload = {
          doctor_id: formData.doctor_id,
          exception_date: formData.exception_date,
          exception_type: formData.exception_type,
          time_slots: formData.time_slots,
          reason: formData.reason,
        };
        if (editingItem) {
          await updateDoctorExceptionById(editingItem.id, payload);
          showAlert('Excepción actualizada exitosamente', 'success');
        } else {
          await addDoctorException(payload);
          showAlert('Excepción añadida exitosamente', 'success');
        }
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      showAlert('Error al guardar la disponibilidad/excepción', 'error');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (itemId, type) => {
    // Modified to accept itemId and type
    if (
      window.confirm(
        `¿Estás seguro de que quieres eliminar esta ${type === 'availability' ? 'disponibilidad' : 'excepción'}?`
      )
    ) {
      try {
        if (type === 'availability') {
          await deleteDoctorAvailabilityById(itemId);
          showAlert('Disponibilidad eliminada exitosamente', 'success');
        } else if (type === 'exception') {
          await deleteDoctorExceptionById(itemId);
          showAlert('Excepción eliminada exitosamente', 'success');
        }
        loadData();
      } catch (error) {
        showAlert(
          `Error al eliminar la ${type === 'availability' ? 'disponibilidad' : 'excepción'}`,
          'error'
        );
      }
    }
  };

  const handleOpenViewModal = (doctor) => {
    setViewDoctor(doctor);
    setModalViewOpen(true);
  };

  const handleCloseViewModal = () => {
    setModalViewOpen(false);
    setViewDoctor(null);
  };

  const getDoctorRecurringAvailability = (doctorId) => {
    return availability.filter((avail) => avail.doctor_id === doctorId);
  };

  const getDoctorExceptionsList = (doctorId) => {
    return exceptions.filter((exp) => exp.doctor_id === doctorId);
  };

  const getDayLabel = (day_of_week) => {
    return DAYS_OF_WEEK.find((d) => Number(d.value) === day_of_week)?.label;
  };

  const formatTime = (time) => {
    return time ? time.substring(0, 5) : '';
  };

  // eslint-disable-next-line no-unused-vars
  const getDoctorName = (doctorId) => {
    return doctors.find((d) => d.id === doctorId)?.name || 'Doctor desconocido';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 3, pb: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Skeleton
            variant="text"
            width={300}
            height={60}
            sx={{ mx: 'auto' }}
          />
          <Skeleton
            variant="text"
            width={200}
            height={30}
            sx={{ mx: 'auto' }}
          />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 3, pb: 3 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Gestión de Disponibilidad Médica
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administra los horarios y disponibilidad de los doctores
        </Typography>
      </Box>

      {/* New prominent buttons for adding availability */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal(null, null, 'availability')}
          sx={{ borderRadius: 2 }}
        >
          Añadir Disponibilidad Recurrente
        </Button>
        <Button
          variant="outlined"
          startIcon={<MedicalServicesIcon />}
          onClick={() => handleOpenModal(null, null, 'exception')}
          sx={{ borderRadius: 2 }}
        >
          Añadir Excepción de Fecha
        </Button>
      </Box>

      {doctors.length === 0 ? (
        <Card sx={{ textAlign: 'center', p: 3 }}>
          <MedicalServicesIcon
            sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            No hay doctores registrados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Primero debes registrar doctores para gestionar su disponibilidad
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {doctors.map((doctor) => {
            const doctorRecurringAvailability = getDoctorRecurringAvailability(
              doctor.id
            );
            const doctorExceptions = getDoctorExceptionsList(doctor.id);
            const hasAvailability =
              doctorRecurringAvailability.length > 0 ||
              doctorExceptions.length > 0;
            return (
              <Grid
                item
                xs={12}
                md={6}
                key={doctor.id}
                sx={{ display: 'flex' }}
              >
                <Card
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                    }
                    action={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={() => handleOpenViewModal(doctor)}>
                          <CalendarTodayIcon />
                        </IconButton>
                      </Box>
                    }
                    title={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%',
                          marginRight: '40px',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {doctor.name}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    subheader={
                      <Box
                        sx={{
                          flex: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          height: '50%',
                          minHeight: '50%',
                        }}
                      >
                        {!hasAvailability ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={doctor.speciality || 'Medicina General'}
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={'Sin horarios configurados'}
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        ) : (
                          <Chip
                            label={doctor.specialty || 'Medicina General'}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                  />
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Fade in={modalOpen}>
          <Card
            sx={{
              minWidth: 400,
              maxWidth: 500,
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: 3,
              boxShadow: 24,
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {formType === 'availability' ? (
                    <AccessTimeIcon color="primary" />
                  ) : (
                    <CalendarTodayIcon color="primary" />
                  )}
                  <Typography variant="h6">
                    {editingItem
                      ? formType === 'availability'
                        ? 'Editar Disponibilidad'
                        : 'Editar Excepción'
                      : formType === 'availability'
                        ? 'Nueva Disponibilidad'
                        : 'Nueva Excepción'}
                  </Typography>
                </Box>
              }
              action={
                <IconButton onClick={handleCloseModal}>
                  <CloseIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Doctor</InputLabel>
                    <Select
                      value={formData.doctor_id}
                      label="Doctor"
                      onChange={(e) =>
                        setFormData({ ...formData, doctor_id: e.target.value })
                      }
                      disabled={editingItem !== null}
                    >
                      {doctors.map((doctor) => (
                        <MenuItem key={doctor.id} value={doctor.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: 'primary.main',
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Typography>{doctor.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formType === 'availability' && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Día de la Semana</InputLabel>
                        <Select
                          value={formData.day_of_week}
                          label="Día de la Semana"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              day_of_week: e.target.value,
                            })
                          }
                        >
                          {DAYS_OF_WEEK.map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <CalendarTodayIcon
                                  sx={{ fontSize: 16, color: 'text.secondary' }}
                                />
                                <Typography>{day.label}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Hora de Inicio</InputLabel>
                        <Select
                          value={formData.hstart}
                          label="Hora de Inicio"
                          onChange={(e) =>
                            setFormData({ ...formData, hstart: e.target.value })
                          }
                        >
                          {TIME_SLOTS.map((time) => (
                            <MenuItem key={time} value={time}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <AccessTimeIcon
                                  sx={{ fontSize: 16, color: 'text.secondary' }}
                                />
                                <Typography>{time}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Hora de Fin</InputLabel>
                        <Select
                          value={formData.hend}
                          label="Hora de Fin"
                          onChange={(e) =>
                            setFormData({ ...formData, hend: e.target.value })
                          }
                        >
                          {TIME_SLOTS.map((time) => (
                            <MenuItem key={time} value={time}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <AccessTimeIcon
                                  sx={{ fontSize: 16, color: 'text.secondary' }}
                                />
                                <Typography>{time}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Fecha de Inicio (Opcional)"
                        type="date"
                        value={formData.start_date || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            start_date: e.target.value,
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Fecha de Fin (Opcional)"
                        type="date"
                        value={formData.end_date || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, end_date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Consultorio/Unidad"
                        value={formData.unit}
                        onChange={(e) =>
                          setFormData({ ...formData, unit: e.target.value })
                        }
                        placeholder="Ej: 1"
                        InputProps={{
                          startAdornment: (
                            <LocationOnIcon
                              sx={{ color: 'text.secondary', mr: 1 }}
                            />
                          ),
                        }}
                      />
                    </Grid>

                    {formData.hstart &&
                      formData.hend &&
                      formData.hstart >= formData.hend && (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            La hora de inicio debe ser anterior a la hora de fin
                          </Alert>
                        </Grid>
                      )}
                    {formData.start_date &&
                      formData.end_date &&
                      formData.start_date > formData.end_date && (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            La fecha de inicio debe ser anterior o igual a la
                            fecha de fin
                          </Alert>
                        </Grid>
                      )}
                  </>
                )}

                {formType === 'exception' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Fecha de Excepción"
                        type="date"
                        value={formData.exception_date || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            exception_date: e.target.value,
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Tipo de Excepción</InputLabel>
                        <Select
                          value={formData.exception_type}
                          label="Tipo de Excepción"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              exception_type: e.target.value,
                            })
                          }
                        >
                          <MenuItem value={'available_override'}>
                            Disponible (Sobrescribe)
                          </MenuItem>
                          <MenuItem value={'unavailable_full_day'}>
                            No Disponible (Día Completo)
                          </MenuItem>
                          <MenuItem value={'unavailable_partial_day'}>
                            No Disponible (Parcial)
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(formData.exception_type === 'available_override' ||
                      formData.exception_type ===
                        'unavailable_partial_day') && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                          Horarios Específicos
                        </Typography>
                        {formData.time_slots.map((slot, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              gap: 2,
                              mb: 1,
                              alignItems: 'center',
                            }}
                          >
                            <FormControl sx={{ width: 120 }}>
                              <InputLabel>Inicio</InputLabel>
                              <Select
                                value={slot.hstart}
                                label="Inicio"
                                onChange={(e) => {
                                  const newSlots = [...formData.time_slots];
                                  newSlots[index].hstart = e.target.value;
                                  setFormData({
                                    ...formData,
                                    time_slots: newSlots,
                                  });
                                }}
                              >
                                {TIME_SLOTS.map((time) => (
                                  <MenuItem key={time} value={time}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl sx={{ width: 120 }}>
                              <InputLabel>Fin</InputLabel>
                              <Select
                                value={slot.hend}
                                label="Fin"
                                onChange={(e) => {
                                  const newSlots = [...formData.time_slots];
                                  newSlots[index].hend = e.target.value;
                                  setFormData({
                                    ...formData,
                                    time_slots: newSlots,
                                  });
                                }}
                              >
                                {TIME_SLOTS.map((time) => (
                                  <MenuItem key={time} value={time}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              sx={{ flexGrow: 1 }}
                              label="Unidad"
                              value={slot.unit}
                              onChange={(e) => {
                                const newSlots = [...formData.time_slots];
                                newSlots[index].unit = e.target.value;
                                setFormData({
                                  ...formData,
                                  time_slots: newSlots,
                                });
                              }}
                            />
                            <IconButton
                              color="error"
                              onClick={() => {
                                const newSlots = formData.time_slots.filter(
                                  (_, i) => i !== index
                                );
                                setFormData({
                                  ...formData,
                                  time_slots: newSlots,
                                });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              time_slots: [
                                ...formData.time_slots,
                                { hstart: '', hend: '', unit: '' },
                              ],
                            })
                          }
                          sx={{ mt: 1 }}
                        >
                          Añadir Horario
                        </Button>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Razón (Opcional)"
                        value={formData.reason}
                        onChange={(e) =>
                          setFormData({ ...formData, reason: e.target.value })
                        }
                        multiline
                        rows={2}
                      />
                    </Grid>
                    {formData.time_slots.some(
                      (slot) =>
                        slot.hstart && slot.hend && slot.hstart >= slot.hend
                    ) && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          La hora de inicio de un horario debe ser anterior a la
                          hora de fin
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 3,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{ borderRadius: 2 }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  startIcon={<SaveIcon />}
                  disabled={
                    !formData.doctor_id ||
                    (formType === 'availability' &&
                      (!formData.day_of_week ||
                        !formData.hstart ||
                        !formData.hend ||
                        formData.hstart >= formData.hend ||
                        (formData.start_date &&
                          formData.end_date &&
                          formData.start_date > formData.end_date))) ||
                    (formType === 'exception' &&
                      (!formData.exception_date ||
                        !formData.exception_type ||
                        ((formData.exception_type === 'available_override' ||
                          formData.exception_type ===
                            'unavailable_partial_day') &&
                          (!formData.time_slots.length ||
                            formData.time_slots.some(
                              (slot) =>
                                !slot.hstart ||
                                !slot.hend ||
                                slot.hstart >= slot.hend
                            )))))
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {editingItem ? 'Actualizar' : 'Guardar'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Modal>
      <Modal
        open={modalViewOpen}
        onClose={handleCloseViewModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Fade in={modalViewOpen}>
          <Card
            sx={{
              minWidth: 400,
              maxWidth: 500,
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: 3,
              boxShadow: 24,
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon color="primary" />
                  <Typography variant="h6">
                    Disponibilidad de {viewDoctor?.name}
                  </Typography>
                </Box>
              }
              action={
                <IconButton onClick={handleCloseViewModal}>
                  <CloseIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              {viewDoctor ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Disponibilidad Recurrente
                  </Typography>
                  {getDoctorRecurringAvailability(viewDoctor.id).length ===
                  0 ? (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      No hay horarios recurrentes configurados.
                    </Typography>
                  ) : (
                    <List disablePadding sx={{ mb: 2 }}>
                      {getDoctorRecurringAvailability(viewDoctor.id).map(
                        (avail, index) => (
                          <React.Fragment key={avail.id}>
                            <ListItem
                              secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() =>
                                      handleOpenModal(
                                        viewDoctor,
                                        avail,
                                        'availability'
                                      )
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    color="error"
                                    onClick={() =>
                                      handleDelete(avail.id, 'availability')
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <ListItemIcon>
                                <AccessTimeIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${getDayLabel(avail.day_of_week)} | ${formatTime(
                                  avail.hstart
                                )} - ${formatTime(avail.hend)}`}
                                secondary={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Unidad: {avail.unit}
                                    </Typography>
                                    {avail.start_date && avail.end_date && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Válido desde:{' '}
                                        {new Date(
                                          avail.start_date
                                        ).toLocaleDateString()}{' '}
                                        hasta:{' '}
                                        {new Date(
                                          avail.end_date
                                        ).toLocaleDateString()}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index <
                              getDoctorRecurringAvailability(viewDoctor.id)
                                .length -
                                1 && <Divider component="li" />}
                          </React.Fragment>
                        )
                      )}
                    </List>
                  )}

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Excepciones de Fecha
                  </Typography>
                  {getDoctorExceptionsList(viewDoctor.id).length === 0 ? (
                    <Typography color="text.secondary">
                      No hay excepciones de fecha configuradas.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {getDoctorExceptionsList(viewDoctor.id).map(
                        (exception, index) => (
                          <React.Fragment key={exception.id}>
                            <ListItem
                              secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() =>
                                      handleOpenModal(
                                        viewDoctor,
                                        exception,
                                        'exception'
                                      )
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    color="error"
                                    onClick={() =>
                                      handleDelete(exception.id, 'exception')
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <ListItemIcon>
                                <CalendarTodayIcon color="secondary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${new Date(exception.exception_date).toISOString().split('T')[0]} - ${exception.exception_type}`}
                                secondary={
                                  <Box>
                                    {exception.time_slots &&
                                      exception.time_slots.length > 0 && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Horarios:{' '}
                                          {exception.time_slots
                                            .map(
                                              (ts) =>
                                                `${formatTime(ts.hstart)}-${formatTime(ts.hend)} (Unidad: ${ts.unit})`
                                            )
                                            .join('; ')}
                                        </Typography>
                                      )}
                                    {exception.reason && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Razón: {exception.reason}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index <
                              getDoctorExceptionsList(viewDoctor.id).length -
                                1 && <Divider component="li" />}
                          </React.Fragment>
                        )
                      )}
                    </List>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </Container>
  );
}
